<script setup>
import { Head, Link, useForm } from "@inertiajs/vue3";
import JetAuthenticationCard from "@/Components/Cards/AuthenticationCard.vue";
import JetAuthenticationCardLogo from "@/Components/AuthenticationCardLogo.vue";
import JetButton from "@/Components/Buttons/Button.vue";
import JetInput from "@/Components/Input/Input.vue";
import JetInputError from "@/Components/Input/InputError.vue";
import JetCheckbox from "@/Components/Input/Checkbox.vue";
import JetLabel from "@/Components/Input/Label.vue";
import { defineAsyncComponent, ref } from "vue";
import AuthProvider from "@/Components/Buttons/AuthProvider.vue";
const ReCaptcha2 = defineAsyncComponent(() =>
    import("@/Components/Input/ReCaptcha2.vue")
);
const props = defineProps({
    canResetPassword: Boolean,
    status: String,
    fromDialog: { type: Boolean, default: false },
});

const emit = defineEmits(["close"]);

const closeDialog = () => {
    emit("close");
};

const loginForm = ref(null);
const visible = ref(false);

const form = useForm({
    email: "",
    password: "",
    "g-recaptcha-response": "",
    remember: true,
});

const submit = () => {
    form.clearErrors();
    form
        .transform((data) => ({
            ...data,
            remember: form.remember ? "on" : "",
        }))
        .post(route("login"), {
            onFinish: () => {
                form.reset("password", "g-recaptcha-response");
            },
            onSuccess: () => {
                if (props.fromDialog) {
                    closeDialog();
                }
                location.reload();
            },
        });
};

const verifyMethod = (response) => {
    form["g-recaptcha-response"] = response;
};
</script>

<template>

    <Head :title="__t('login')" />
    <!-- test -->
    <JetAuthenticationCard class="" :fromDialog="fromDialog">
        <div v-if="status" class="mb-4 text-sm font-medium text-green-600">
            {{ status }}
        </div>
        <div>
            <div class="flex" :class="fromDialog ? 'justify-between' : 'justify-center'">
                <h1 class="font-bold text-[22px] text-center h1" :class="fromDialog ? '' : 'hidden md:block'">
                    {{ __t("login") }}
                </h1>
                <v-btn @click="closeDialog" v-if="fromDialog" icon="mdi-close"
                    :class="fromDialog ? '' : 'hidden'"></v-btn>
            </div>
            <br />

            <div class="flex flex-col justify-center gap-2 md:flex-row">
                <auth-provider />
            </div>
            <br />

            <hr />
            <br />
            <h6 class="mb-5 text-start h5">{{ __t("or_use_your_account") }}</h6>
        </div>

        <form @submit.prevent="submit" ref="loginForm">
            <div>
                <v-text-field :label="__t('email')" type="email" name="email" variant="outlined" required
                    v-model="form.email"></v-text-field>
                <JetInputError class="mt-2" :message="form.errors.email" />
            </div>

            <div class="mt-4">
                <JetInputError class="mt-2" :message="form.errors.password" />
                <v-text-field :label="__t('password')" :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
                    :type="visible ? 'text' : 'password'" variant="outlined" required v-model="form.password"
                    @click:append-inner="visible = !visible"></v-text-field>
            </div>
            <v-no-ssr>
                <ReCaptcha2 @verified="verifyMethod" :reset="!!form.errors['g-recaptcha-response']">
                </ReCaptcha2>
            </v-no-ssr>

            <div class="block mt-4">
                <label class="flex items-center">
                    <JetCheckbox v-model:checked="form.remember" name="remember" />
                    <span class="mx-2 text-sm text-gray-600 dark:text-white">{{
                        __t("remember_me")
                    }}</span>
                </label>
            </div>
            <JetInputError class="mt-2" :message="form.errors['g-recaptcha-response']" />

            <div class="flex flex-col flex-wrap items-center justify-between mt-4 md:flex-row">
                <div v-if="route().has('register')">
                    <Link :href="route('register')" v-if="!fromDialog"
                        class="font-bold text-gray-600 text-s m dark:text-white hover:text-gray-900">
                    {{ __t("register_new_account") }}
                    </Link>
                </div>

                <div class="flex items-center justify-between w-full mt-4 mt-md-1 md:justify-end md:w-auto">
                    <Link v-if="canResetPassword" :href="route('password.request')"
                        class="text-sm text-gray-600 underline dark:text-white hover:text-gray-900">
                    {{ __t("forgot_your_password") }}
                    </Link>

                    <JetButton class="mx-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        {{ __t("login") }}
                    </JetButton>
                </div>
            </div>
        </form>
    </JetAuthenticationCard>
</template>

<style scoped>
input:not([type="checkbox"]) {
    height: 55px;
    background-color: rgb(var(--v-theme-input-background));
    padding: 12px;
    border: 1px solid rgb(var(--v-theme-border));
}

.bblack-color-regester {
    color: black !important;
}
</style>
